import { HStack, RadioGroup, Text, VStack } from '@chakra-ui/react';
import { useStatisticMapContext } from '@components/map/MapContext';
import { DraggablePanel } from '@components/ui/draggable';
import { InputNumber } from '@components/ui/input/InputNumber';
import { DEFAULT_THRESHOLD } from '@utils/map/map.constants';
import { RefObject } from 'react';
import { FormattedMessage } from 'react-intl';

import { StatisticFilterColorItem, StatisticFilterColorMode } from '@/types/map.types';

import PopupContainer from './common/PopupContainer';

type Props = {
  mapConstraintsRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
};

const filterColorItems: StatisticFilterColorItem[] = [
  {
    label: 'statisticFilters.color.byMaxAlertLevel',
    value: 'byMaxAlertLevel',
  },
  {
    label: 'statisticFilters.color.byTrajectory',
    value: 'byTrajectory',
  },
  {
    label: 'statisticFilters.color.byMaxHeight',
    value: 'byMaxHeight',
  },
];

export function StatisticPopup({ mapConstraintsRef, onClose }: Readonly<Props>) {
  const { filterColorMode, setFilterColorMode, threshold, setThreshold } = useStatisticMapContext();

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={2} left={10}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="statisticFilters.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width="501px">
        <PopupContainer>
          <VStack gap="standardXS" alignItems="start" width="100%">
            <Text color="neutral.300" textStyle="screenStd">
              <FormattedMessage id="statisticFilters.color.description" />
            </Text>
            <RadioGroup.Root
              value={filterColorMode}
              size="screenStd"
              onValueChange={({ value }) => setFilterColorMode(value as StatisticFilterColorMode)}
            >
              {filterColorItems.map((item) => (
                <RadioGroup.Item key={item.value} value={item.value}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>
                    <FormattedMessage id={item.label} />
                  </RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </RadioGroup.Root>
            <HStack gap="standardXS" marginLeft={5}>
              <Text color="neutral.300" textStyle="screenStd">
                <FormattedMessage id="statisticFilters.color.threshold" />
              </Text>
              <InputNumber
                value={threshold}
                disabled={filterColorMode !== 'byMaxHeight'}
                onChange={(value) => setThreshold(value ?? DEFAULT_THRESHOLD)}
                suffix="m"
                width="230px"
              />
            </HStack>
          </VStack>
        </PopupContainer>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
